import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Play, X } from 'lucide-react';
import type { TutorialType } from './TutorialProvider';
import { cn } from '@/lib/utils';
import logoImage from '@/assets/chopperswap-logo-no-bg.png';

interface TutorialWelcomeProps {
  isOpen: boolean;
  tutorialType: TutorialType | null;
  stepCount?: number;
  onStart: () => void;
  onSkip: () => void;
}

const tutorialInfo: Record<TutorialType, { title: string; description: string }> = {
  home: {
    title: 'Welcome to ChopperSwap!',
    description: 'Take a quick tour to discover how to buy, sell, rent, or lease helicopters, engines, and parts with ease.',
  },
  marketplace: {
    title: 'Explore the Marketplace',
    description: 'Learn how to search, filter, and find exactly what you\'re looking for in our comprehensive marketplace.',
  },
  helicopterWizard: {
    title: 'Create a Helicopter Listing',
    description: 'We\'ll guide you through creating a professional listing that attracts serious buyers.',
  },
  engineWizard: {
    title: 'Create an Engine Listing',
    description: 'Learn how to showcase your engine with detailed specifications and module tracking.',
  },
  partsWizard: {
    title: 'Create a Parts Listing',
    description: 'Discover how to list parts and tools with shipping options and compatibility info.',
  },
  profile: {
    title: 'Your Dashboard',
    description: 'Learn how to manage your listings, drafts, privacy settings, and more from your profile.',
  },
  messaging: {
    title: 'Messaging Center',
    description: 'See how to communicate securely with buyers and sellers.',
  },
  verification: {
    title: 'Get Verified!',
    description: 'Learn how to verify your profile through LinkedIn and earn the trusted verification badge.',
  },
  savedCompare: {
    title: 'Saved & Compare',
    description: 'Learn how to save favorite listings and compare them side-by-side.',
  },
  jobWizard: {
    title: 'Create a Job Listing',
    description: 'We\'ll walk you through posting a job, building a professional profile, or finding aviation talent.',
  },
  serviceWizard: {
    title: 'Create a Service Listing',
    description: 'Learn how to showcase your MRO, training, or aviation services to attract qualified inquiries.',
  },
  privacy: {
    title: 'Privacy & Data Control',
    description: 'Discover how to protect your sensitive data. Control who sees your prices, locations, contact info, and documents.',
  },
  alerts: {
    title: 'Alerts & Notifications',
    description: 'Set up custom alerts to get notified when listings matching your criteria appear on the marketplace.',
  },
  listingManagement: {
    title: 'Listing Management',
    description: 'Learn how to manage, edit, and track all your listings from the unified dashboard.',
  },
};

export const TutorialWelcome: React.FC<TutorialWelcomeProps> = ({
  isOpen,
  tutorialType,
  stepCount,
  onStart,
  onSkip,
}) => {
  const info = tutorialType ? tutorialInfo[tutorialType] : null;

  if (!info) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onSkip()}>
      <DialogContent className="sm:max-w-md overflow-hidden border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl">
        <DialogHeader className="text-center relative pt-6">
          {/* Clean logo container - no blue background */}
          <div className={cn(
            "mx-auto w-24 h-24 rounded-2xl flex items-center justify-center mb-4",
            "bg-slate-50 dark:bg-slate-800",
            "border border-slate-200 dark:border-slate-700",
            "shadow-sm"
          )}>
            <img 
              src={logoImage} 
              alt="ChopperSwap" 
              className="w-16 h-16 object-contain dark:brightness-0 dark:invert"
            />
          </div>
          <DialogTitle className="text-2xl font-bold text-slate-900 dark:text-white">
            {info.title}
          </DialogTitle>
          <DialogDescription className="text-base mt-2 text-slate-600 dark:text-slate-300">
            {info.description}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-center gap-3 py-4">
          <div className="flex gap-1.5">
            {[1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className="w-2 h-2 rounded-full bg-primary animate-pulse"
                style={{ animationDelay: `${i * 150}ms` }}
              />
            ))}
          </div>
          <span className="text-sm text-slate-600 dark:text-slate-400 font-medium">
            {stepCount ? `${stepCount}-step interactive tour` : 'Interactive guided tour'}
          </span>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2 relative">
          <Button
            variant="ghost"
            onClick={onSkip}
            className="gap-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          >
            <X className="w-4 h-4" />
            Skip for now
          </Button>
          <Button
            onClick={onStart}
            className={cn(
              "gap-2 font-semibold text-white",
              "bg-primary hover:bg-primary/90",
              "shadow-lg shadow-primary/30",
              "transition-all duration-300"
            )}
          >
            <Play className="w-4 h-4" />
            Start Tour
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};